<template>
  <div class="w-full xl:w-1/2 rounded-xl bg-white py-6 px-8">
    <div
      class="flex items-center justify-between w-full mb-8 py-3 border-b border-truegray-300"
    >
      <h5>Your Connections</h5>
      <InputDropdownListSelector
        :selected-option="sortingSelectedOption"
        :options="CD_CONNECTIONS_SORT_BY_OPTIONS"
        @on-submit="updateSorting"
      />
    </div>
    <template v-if="connections?.length">
      <!-- Connections data -->
      <div
        v-for="(item, i) in connections"
        :key="i"
        :class="connectionsLoading ? 'opacity-30' : 'opacity-100'"
        class="grid grid-cols-12 items-center gap-x-2 mb-4 sm:mb-2"
      >
        <div class="col-span-12 sm:col-span-5">
          <ListConnection :connection="item" />
        </div>
        <div class="col-span-2 sm:col-span-2">
          <div
            title="Connection views"
            class="flex items-center text-cw-900 space-x-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-4 h-4"
            >
              <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
              <path
                fill-rule="evenodd"
                d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
                clip-rule="evenodd"
              />
            </svg>

            <p class="font-light space-x-2 mb-0">
              {{ formatNumber(item.statistics.displayed) }}
            </p>
          </div>
        </div>
        <div class="col-span-2 sm:col-span-2">
          <div
            title="Connection total leads"
            class="flex items-center text-cw-900 space-x-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-4 h-4"
            >
              <path
                d="M10 1a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 1ZM5.05 3.05a.75.75 0 0 1 1.06 0l1.062 1.06A.75.75 0 1 1 6.11 5.173L5.05 4.11a.75.75 0 0 1 0-1.06ZM14.95 3.05a.75.75 0 0 1 0 1.06l-1.06 1.062a.75.75 0 0 1-1.062-1.061l1.061-1.06a.75.75 0 0 1 1.06 0ZM3 8a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 3 8ZM14 8a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 14 8ZM7.172 10.828a.75.75 0 0 1 0 1.061L6.11 12.95a.75.75 0 0 1-1.06-1.06l1.06-1.06a.75.75 0 0 1 1.06 0ZM10.766 7.51a.75.75 0 0 0-1.37.365l-.492 6.861a.75.75 0 0 0 1.204.65l1.043-.799.985 3.678a.75.75 0 0 0 1.45-.388l-.978-3.646 1.292.204a.75.75 0 0 0 .74-1.16l-3.874-5.764Z"
              />
            </svg>

            <p class="font-light space-x-2 mb-0">
              {{ formatNumber(item.statistics.viewed) }}
            </p>
          </div>
        </div>
        <div class="col-span-2 sm:col-span-2">
          <div
            title="Connection added date"
            class="flex items-center space-x-2 text-cw-900"
          >
            <img
              src="/img/icons/contributor_dashboard/calendar-dark.svg"
              alt="Calendar icon"
            />

            <p class="text-xs font-light m-0">
              {{ item.created_at_ui }}
            </p>
          </div>
        </div>
        <div class="col-span-2 sm:col-span-1">
          <button
            class="btn px-2 py-1 mb-0 ml-auto text-cw-700 hover:text-cw-alt hover:rotate-360 transition-all duration-300"
            title="More connection stats"
            @click="showConnectionStatsModal(item)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-4 h-4"
            >
              <path
                fill-rule="evenodd"
                d="M15.28 9.47a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L13.69 10 9.97 6.28a.75.75 0 0 1 1.06-1.06l4.25 4.25ZM6.03 5.22l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L8.69 10 4.97 6.28a.75.75 0 0 1 1.06-1.06Z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
      <!-- /Connections data -->

      <!-- Pagination -->
      <div
        v-if="canShowConnectionsPagination"
        class="flex justify-between items-center mt-4 h-10"
      >
        <span
          :disabled="isFirstPageConnections || connectionsLoading"
          :class="[
            isFirstPageConnections || connectionsLoading
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="
            getConnections({ page: connectionsPagination.current_page - 1 })
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 17l-5-5m0 0l5-5m-5 5h12"
            />
          </svg>
        </span>
        <span v-if="!connectionsLoading" class="text-center text-xs">
          Page {{ connectionsPagination.current_page }} of
          {{ connectionsPagination.last_page }}
        </span>
        <span v-else class="w-8 h-8 flex items-center justify-center">
          <lottie :options="loadingAnimationOptions" />
        </span>
        <span
          :disabled="isLastPageConnections || connectionsLoading"
          :class="[
            isLastPageConnections || connectionsLoading
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="
            getConnections({ page: connectionsPagination.current_page + 1 })
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </span>
      </div>
      <!-- /Pagination -->
    </template>
    <div v-else class="flex justify-center pb-2">
      <span class="text-sm">No connections</span>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { EventBus } from '../../../js/plugins/event-bus.js'
import InputDropdownListSelector from './InputDropdownListSelector.vue'
import ListConnection from '../../shared/connections/components/ListConnection.vue'
import { formatNumber } from '../../../js/util/helper.js'
import { CD_CONNECTIONS_SORT_BY_OPTIONS } from '../../../js/util/constants.js'

export default defineComponent({
  name: 'StatisticsCardConnections',

  components: {
    InputDropdownListSelector,
    ListConnection,
  },

  setup() {
    const store = useStore()

    // Connections list stats
    const connectionsLoading = ref(false)
    const connections = computed(
      () => store.getters['contributors/getConnectionsWithStats'],
    )
    const connectionsPagination = computed(
      () => store.getters['contributors/getConnectionsPagination'],
    )
    const canShowConnectionsPagination = computed(
      () => connectionsPagination.value?.last_page > 1,
    )
    const isFirstPageConnections = computed(() => {
      return connectionsPagination?.value?.current_page === 1
    })

    const isLastPageConnections = computed(() => {
      return (
        connectionsPagination?.value?.current_page ===
        connectionsPagination?.value?.last_page
      )
    })

    const sortingSelectedOption = ref(CD_CONNECTIONS_SORT_BY_OPTIONS[0])

    const updateSorting = (opt) => {
      sortingSelectedOption.value = opt
    }

    watch(sortingSelectedOption, () => getConnections({ page: 1 }))

    const getConnections = async ({
      page = pagination.value?.current_page,
      sortBy = sortingSelectedOption.value?.sortBy,
      orderBy = sortingSelectedOption.value?.orderBy,
    }) => {
      connectionsLoading.value = true
      const payload = {
        page,
        sortBy,
        orderBy,
      }
      await store.dispatch(
        'contributors/dispatchUpdateStatsConnections',
        payload,
      )
      connectionsLoading.value = false
    }

    const showConnectionStatsModal = (item) => {
      EventBus.$emit('showModal', {
        action: 'cw-contributor-connection-stats',
        data: {
          connection: item,
        },
      })
    }

    return {
      formatNumber,

      // Connections
      connectionsLoading,
      connections,
      connectionsPagination,
      canShowConnectionsPagination,
      isFirstPageConnections,
      isLastPageConnections,
      getConnections,
      showConnectionStatsModal,

      CD_CONNECTIONS_SORT_BY_OPTIONS,
      sortingSelectedOption,
      updateSorting,
    }
  },
})
</script>
